import { breakpoints, Heading, Inline, Stack } from '@etg/wings';
import { useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Pictogram } from '@eti/components';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { css } from '@eti/styles';
import { CancellationRefundOfferReason, PartOfExternalFlights } from '@eti/schema-types';
import { isAfter } from 'date-fns';
import TitledCard from '../../../../common/components/TitledCard';
import { useChat } from '../../../../common/chat/ChatContext';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import { scrollToElement } from '../../../../utils/scrollToElement';
import Topic from './Topic';
import NameCorrection from './subtopics/NameCorrection';
import AddPassenger from './subtopics/AddPassenger';
import AddServices from './subtopics/AddServices';
import CheckInInfo from './subtopics/CheckInInfo';
import CantFindMyAnswer from './subtopics/CantFindMyAnswer';
import ReceiptAndBookingConfirmation from './subtopics/ReceiptAndBookingConfirmation';
import Cancellation from './subtopics/Cancellation';
import Refund from './subtopics/Refund';
import Rebooking from './subtopics/Rebooking';
import BookingDetails from './subtopics/BookingDetails';
import FlightScheduleChange from './subtopics/FlightScheduleChange';
import RyanairRefund from './subtopics/RyanairRefund';
import RyanairCancellation from './subtopics/RyanairCancellation';

const topicButtonSpacing = 16;

const inlineStyles = css`
  gap: 24px ${topicButtonSpacing}px;
`;

const topicButtonStyles = css`
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    width: calc((100% - 1 * ${topicButtonSpacing}px) / 2);
  }

  @media (min-width: ${breakpoints._1024}) {
    width: calc((100% - 3 * ${topicButtonSpacing}px) / 4);
  }
`;

const noFocusStyles = css`
  &:focus-visible {
    outline: none;
  }
`;

type SubtopicType = {
  content: ReactNode;
  id: string;
  title: string;
  snowplow: { label: string; property?: string };
};

type TopicsType = {
  iconId: string;
  id: string;
  title: string;
  subtopics: SubtopicType[];
  snowplowLabel: string;
};

const Topics = () => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const subtopicHeaderRef = useRef<HTMLHeadingElement | null>(null);
  const contentHeaderRef = useRef<HTMLHeadingElement | null>(null);
  const [selectedTopicIndex, setSelectedTopicIndex] = useState<number | null>(null);
  const [selectedSubtopicIndex, setSelectedSubtopicIndex] = useState<number | null>(null);
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const { setGenesysExtraInfo } = useChat();
  const {
    isOrderActive,
    partOfExternalFlights,
    cancellationOptionsData,
    cancellationOptionsMutation,
  } = useOrderDetails();

  useEffect(() => {
    if (isOrderActive && !cancellationOptionsData) {
      cancellationOptionsMutation();
    }
  }, [cancellationOptionsData, cancellationOptionsMutation, isOrderActive]);

  const scheduleChangeRefundOffer =
    cancellationOptionsData?.cancellationOptions?.refundOffers?.find(
      (refundOffer) =>
        refundOffer.refundOfferReason === CancellationRefundOfferReason.ScheduleChangeRefund,
    );
  const hasScheduleChangeRefundOffer = Boolean(scheduleChangeRefundOffer);
  const isScheduleChangeOfferExpired = Boolean(
    scheduleChangeRefundOffer &&
      !isAfter(scheduleChangeRefundOffer.expirationDateTimeUTC, new Date().toISOString()),
  );

  const topics: TopicsType[] = [
    {
      iconId: 'flexible-ticket',
      id: 'cancellation-topic',
      title: t('ContactUs.Topics.Category.Cancellation.Title'),
      snowplowLabel: 'CHANGE_OR_CANCELLATION',
      subtopics: [
        {
          id: 'cancellation-subtopic',
          title: t('ContactUs.Subtopic.Cancellation.Title'),
          content:
            partOfExternalFlights === PartOfExternalFlights.All ? (
              <RyanairCancellation
                hasScheduleChangeRefundOffer={hasScheduleChangeRefundOffer}
                isScheduleChangeOfferExpired={isScheduleChangeOfferExpired}
              />
            ) : (
              <Cancellation />
            ),
          snowplow: {
            label: 'CANCELLATION',
            property: 'CHANGE_OR_CANCELLATION',
          },
        },
        {
          id: 'rebooking-subtopic',
          title: t('ContactUs.Subtopic.Rebooking.Title'),
          content: <Rebooking />,
          snowplow: {
            label: 'CHANGE',
            property: 'CHANGE_OR_CANCELLATION',
          },
        },
        {
          id: 'refund-status-subtopic',
          title: t('ContactUs.Subtopic.RefundStatus.Title'),
          content:
            partOfExternalFlights === PartOfExternalFlights.All && isOrderActive ? (
              <RyanairRefund
                hasScheduleChangeRefundOffer={hasScheduleChangeRefundOffer}
                isScheduleChangeOfferExpired={isScheduleChangeOfferExpired}
              />
            ) : (
              <Refund />
            ),
          snowplow: {
            label: 'REFUND',
            property: 'CHANGE_OR_CANCELLATION',
          },
        },
      ],
    },
    {
      iconId: 'flight-related',
      id: 'flight-related-topic',
      title: t('ContactUs.Topics.Category.Flight.Title'),
      snowplowLabel: 'FLIGHT_AND_BOOKING',
      subtopics: [
        {
          id: 'booking-details-subtopic',
          title: t('ContactUs.Subtopic.BookingDetails.Title'),
          content: <BookingDetails />,
          snowplow: {
            label: 'BOOKING_DETAILS',
            property: 'FLIGHT_AND_BOOKING',
          },
        },
        {
          id: 'booking-confirmation-subtopic',
          title: t('ContactUs.Subtopic.BookingConfirmation.Title'),
          content: <ReceiptAndBookingConfirmation />,
          snowplow: {
            label: 'ETICKET_AND_RECEIPT',
            property: 'FLIGHT_AND_BOOKING',
          },
        },
        {
          id: 'checkin-info-subtopic',
          title: t('ContactUs.Subtopic.CheckInInfo.Title'),
          content: <CheckInInfo />,
          snowplow: {
            label: 'CHECK_IN',
            property: 'FLIGHT_AND_BOOKING',
          },
        },
        {
          id: 'flight-schedule-subtopic',
          title: t('ContactUs.Subtopic.FlightScheduleChange.Title'),
          content: <FlightScheduleChange />,
          snowplow: {
            label: 'AIRLINE_SCHEDULE_CHANGE',
            property: 'FLIGHT_AND_BOOKING',
          },
        },
      ],
    },
    {
      iconId: 'upgrade-trip',
      id: 'upgrade-trip-topic',
      title: t('ContactUs.Topics.Category.Products.Title'),
      snowplowLabel: 'PRODUCTS_AND_SERVICES',
      subtopics: [
        {
          id: 'add-services-subtopic',
          title: t('ContactUs.Subtopic.AddServices.Title'),
          content: <AddServices />,
          snowplow: {
            label: 'ADD_SERVICES',
            property: 'PRODUCTS_AND_SERVICES',
          },
        },
      ],
    },
    {
      iconId: 'passenger-related',
      id: 'passenger-related-topic',
      title: t('ContactUs.Topics.Category.Passenger.Title'),
      snowplowLabel: 'PASSENGERS',
      subtopics: [
        {
          id: 'name-correction-subtopic',
          title: t('ContactUs.Subtopic.NameCorrection.Title'),
          content: <NameCorrection />,
          snowplow: {
            label: 'MODIFY_NAMES',
            property: 'PASSENGERS',
          },
        },
        {
          id: 'add-passenger-subtopic',
          title: t('ContactUs.Subtopic.AddPassenger.Title'),
          content: <AddPassenger />,
          snowplow: {
            label: 'ADD_PASSENGER',
            property: 'PASSENGERS',
          },
        },
      ],
    },
    {
      iconId: 'question-mark',
      id: 'question-mark-topic',
      title: t('ContactUs.Topics.Category.Other.Title'),
      snowplowLabel: 'OTHER',
      subtopics: [
        {
          id: 'cant-find-my-answer-subtopic',
          title: t('ContactUs.Subtopic.CantFindMyAnswer.Title'),
          content: <CantFindMyAnswer />,
          snowplow: {
            label: 'MORE_QUESTIONS',
            property: 'OTHER',
          },
        },
      ],
    },
  ];

  useEffect(() => {
    if (selectedTopicIndex !== null && subtopicHeaderRef.current) {
      subtopicHeaderRef.current.focus({ preventScroll: true });
      const tabs = document.getElementById('routed-tabs');
      scrollToElement(subtopicHeaderRef.current, tabs?.offsetHeight || 0);
    }
  }, [subtopicHeaderRef, selectedTopicIndex]);

  useEffect(() => {
    if (selectedSubtopicIndex !== null && contentHeaderRef.current) {
      contentHeaderRef.current.focus({ preventScroll: true });
      const tabs = document.getElementById('routed-tabs');
      scrollToElement(contentHeaderRef.current, tabs?.offsetHeight || 0);
    }
  }, [contentHeaderRef, selectedSubtopicIndex]);

  const onSelectTopicHandler = (topic: TopicsType, topicIndex: number) => {
    if (topicIndex === selectedTopicIndex) {
      return;
    }
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: topic.snowplowLabel,
      });
    }
    setSelectedTopicIndex(topicIndex);
    setSelectedSubtopicIndex(null);
  };

  const onSelectSubtopicHandler = (subtopic: SubtopicType, subtopicIndex: number) => {
    setSelectedSubtopicIndex(subtopicIndex);
    setGenesysExtraInfo({ subtopic: subtopic.id });
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: subtopic.snowplow.label,
        property: subtopic.snowplow.property,
      });
    }
  };

  return (
    <TitledCard data-testid="topics-card" title={t('ContactUs.Topics.Header')}>
      <Stack spacing={24}>
        {t('ContactUs.Topics.Description')}
        <Stack spacing={48}>
          <Inline role="list" spacing={topicButtonSpacing}>
            {topics.map((topic, index) => (
              <div className={topicButtonStyles} key={topic.id} role="listitem">
                <Topic
                  aria-controls={`subtopics-topic-${topic.id}`}
                  icon={<Pictogram brand={brand.code} id={topic.iconId} size="3rem" />}
                  id={topic.id}
                  isSelected={selectedTopicIndex === index}
                  onClick={() => onSelectTopicHandler(topic, index)}
                  title={topic.title}
                />
              </div>
            ))}
          </Inline>
          {selectedTopicIndex !== null && (
            <Stack
              data-testid="subtopics-stack"
              id={`subtopics-topic-${topics[selectedTopicIndex].id}`}
              spacing={24}
            >
              <Heading
                as="h3"
                className={noFocusStyles}
                level={2}
                ref={subtopicHeaderRef}
                tabIndex={-1}
              >
                {topics[selectedTopicIndex].title}
              </Heading>
              <Inline className={inlineStyles} role="list">
                {topics[selectedTopicIndex].subtopics.map((subtopic, subtopicIndex) => {
                  return (
                    <div className={topicButtonStyles} key={subtopic.id} role="listitem">
                      <Topic
                        aria-controls={`content-subtopic-${subtopic.id}`}
                        id={subtopic.id}
                        isSelected={selectedSubtopicIndex === subtopicIndex}
                        onClick={() => onSelectSubtopicHandler(subtopic, subtopicIndex)}
                        title={subtopic.title}
                      />
                    </div>
                  );
                })}
              </Inline>
            </Stack>
          )}
          {selectedTopicIndex !== null && selectedSubtopicIndex !== null && (
            <Stack
              id={`content-subtopic-${topics[selectedTopicIndex].subtopics[selectedSubtopicIndex].id}`}
              spacing={24}
            >
              <Heading
                as="h4"
                className={noFocusStyles}
                level={2}
                ref={contentHeaderRef}
                tabIndex={-1}
              >
                {topics[selectedTopicIndex].subtopics[selectedSubtopicIndex].title}
              </Heading>
              {topics[selectedTopicIndex].subtopics[selectedSubtopicIndex].content}
            </Stack>
          )}
        </Stack>
      </Stack>
    </TitledCard>
  );
};

export default Topics;
